import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Home, Receipt, User, Brain } from "lucide-react";
import AddTransactionModal from "./AddTransactionModal";
import VoiceRecorder from "./VoiceRecorder";
import { transactionService } from "../services/transactionService";
import { voiceService } from "../services/voiceService";
import { TransactionContext } from "../context/TransactionContext";

const Footer = () => {
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState("home");
  const [isAddOptionsVisible, setIsAddOptionsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { refreshTransactions } = useContext(TransactionContext);
  const [newTransaction, setNewTransaction] = useState({
    type: "expense",
    amount: "",
    category: "",
    description: "",
    date: new Date().toISOString(),
  });

  const toggleAddOptions = () => {
    setIsAddOptionsVisible(!isAddOptionsVisible);
  };

  // Handle input changes in the modal form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTransaction((prev) => ({
      ...prev,
      [name]: name === "amount" ? parseFloat(value) : value,
    }));
  };

  // Handle manual transaction addition
  const handleAddTransaction = async () => {
    if (!newTransaction.category || !newTransaction.amount) {
      alert("Please fill in all required fields.");
      return;
    }

    try {
      await transactionService.addTransaction({
        ...newTransaction,
        date: new Date().toISOString(),
      });

      alert("Transaction added successfully!");
      setIsModalOpen(false);
      setNewTransaction({
        type: "expense",
        amount: "",
        category: "",
        description: "",
        date: new Date().toISOString(),
      });
      refreshTransactions();
    } catch (error) {
      console.error("Error adding transaction:", error);
      alert("Failed to add transaction.");
    }
  };

  // Handle voice-based transaction addition
  const handleAddTransactionByVoice = async (audioBlob) => {
    try {
      const transactionDetails = await voiceService.processAudio(audioBlob);

      await transactionService.addTransaction({
        ...transactionDetails,
        date: new Date().toISOString(),
      });

      alert("Transaction added successfully!");
      refreshTransactions();
      setIsAddOptionsVisible(false);
    } catch (error) {
      console.error("Error adding transaction by voice:", error);
      alert("Failed to add transaction via voice.");
    }
  };

  return (
    <>
      <AddTransactionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        newTransaction={newTransaction}
        handleInputChange={handleInputChange}
        handleAddTransaction={handleAddTransaction}
      />

      {/* Add Transaction Options */}
      {isAddOptionsVisible && (
        <div
          className="position-fixed start-0 end-0 d-flex justify-content-around"
          style={{
            bottom: "80px",
            zIndex: 1050,
            padding: "0 15px",
          }}
        >
          <div
            className="card shadow-lg p-3 text-center"
            style={{
              backgroundColor: "#f8f9fa",
              width: "45%",
              maxWidth: "180px",
              borderRadius: "15px",
            }}
            onClick={() => {
              setIsAddOptionsVisible(false);
              setIsModalOpen(true);
            }}
          >
            <h6 className="mb-3" style={{ fontSize: "clamp(0.8rem, 2vw, 1rem)" }}>Add Manually</h6>
            <button className="btn btn-outline-primary" style={{ fontSize: "clamp(0.7rem, 1.5vw, 0.9rem)" }}>
              Enter Details
            </button>
          </div>

          <div
            className="card shadow-lg p-3 text-center"
            style={{
              backgroundColor: "#f8f9fa",
              width: "45%",
              maxWidth: "180px",
              borderRadius: "15px",
            }}
          >
            <h6 className="mb-3" style={{ fontSize: "clamp(0.8rem, 2vw, 1rem)" }}>Add by Speaking</h6>
            <VoiceRecorder onTransactionAdded={handleAddTransactionByVoice} />
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <div
        className="bg-white position-fixed bottom-0 start-0 end-0 shadow-lg"
        style={{
          zIndex: 1000,
          paddingBottom: "env(safe-area-inset-bottom, 0px)", // Support for iPhone notch
        }}
      >
        <div className="d-flex justify-content-around align-items-center py-2 px-2">
          {/* Home */}
          <button
            className={`btn d-flex flex-column align-items-center ${
              activePage === "home" ? "text-black" : "text-secondary"
            }`}
            onClick={() => {
              setActivePage("home");
              navigate("/home");
            }}
            style={{ 
              flex: 1,
              minWidth: "60px",
              maxWidth: "80px",
              padding: "5px 0"
            }}
          >
            <Home style={{ width: "clamp(20px, 5vw, 24px)", height: "clamp(20px, 5vw, 24px)" }} />
            <span style={{ fontSize: "clamp(0.7rem, 2vw, 0.875rem)" }} className="mt-1">Home</span>
          </button>

          {/* Transactions */}
          <button
            className={`btn d-flex flex-column align-items-center ${
              activePage === "transaction" ? "text-black" : "text-secondary"
            }`}
            onClick={() => {
              setActivePage("transaction");
              navigate("/transaction");
            }}
            style={{ 
              flex: 1,
              minWidth: "60px",
              maxWidth: "80px",
              padding: "5px 0"
            }}
          >
            <Receipt style={{ width: "clamp(20px, 5vw, 24px)", height: "clamp(20px, 5vw, 24px)" }} />
            <span style={{ fontSize: "clamp(0.7rem, 2vw, 0.875rem)" }} className="mt-1">Transactions</span>
          </button>

          {/* Add Button (FAB) */}
          <div style={{ 
            flex: 1,
            minWidth: "60px",
            maxWidth: "80px",
            display: "flex",
            justifyContent: "center"
          }}>
            <button
              className="btn bg-dark text-white rounded-circle d-flex justify-content-center align-items-center"
              style={{
                width: "clamp(50px, 12vw, 60px)",
                height: "clamp(50px, 12vw, 60px)",
                position: "relative",
                bottom: "clamp(20px, 5vw, 30px)",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              }}
              onClick={toggleAddOptions}
            >
              <i className={`bi ${isAddOptionsVisible ? "bi-x" : "bi-plus"} fs-3`}></i>
            </button>
          </div>

          {/* AI */}
          <button
            className={`btn d-flex flex-column align-items-center ${
              activePage === "ai" ? "text-black" : "text-secondary"
            }`}
            onClick={() => {
              setActivePage("ai");
              navigate("/ai");
            }}
            style={{ 
              flex: 1,
              minWidth: "60px",
              maxWidth: "80px",
              padding: "5px 0"
            }}
          >
            <Brain style={{ width: "clamp(20px, 5vw, 24px)", height: "clamp(20px, 5vw, 24px)" }} />
            <span style={{ fontSize: "clamp(0.7rem, 2vw, 0.875rem)" }} className="mt-1">AI</span>
          </button>

          {/* Profile */}
          <button
            className={`btn d-flex flex-column align-items-center ${
              activePage === "profile" ? "text-black" : "text-secondary"
            }`}
            onClick={() => {
              setActivePage("profile");
              navigate("/profile");
            }}
            style={{ 
              flex: 1,
              minWidth: "60px",
              maxWidth: "80px",
              padding: "5px 0"
            }}
          >
            <User style={{ width: "clamp(20px, 5vw, 24px)", height: "clamp(20px, 5vw, 24px)" }} />
            <span style={{ fontSize: "clamp(0.7rem, 2vw, 0.875rem)" }} className="mt-1">Profile</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Footer;